import { mapUnderwritingMessagesToCriteria } from '@/features/analyses/pme/acceptance-criteria';
import {
  coverageUnitSumInsured,
  sumCoverageSumInsured,
} from '@/features/analyses/pme/coverage-rates';
import { benefitSumInsuredByCoverage } from '@/features/quotes/utils/group-life/benefit-sum-insured.util';
import {
  createLifeCapitalResolver,
  isStoredSalaryCapital,
} from '@/features/analyses/pme/life-capital';
import {
  boundDisplayedCapital,
  resolveMemberDisplayCapital,
} from '@/features/quotes/utils/group-life/effective-capital';
import {
  buildCoverageNameByCode,
  mapCommercializationRows,
  mapDemonstrativeRows,
  mapInsuranceCard,
  mapPremiumValues,
  mapStipulantAccordions,
  mapStipulantCard,
  resolvePlanCoverages,
} from '@/features/proposals/components/pme/utils/hydrate-proposal.util';
import {
  useGroupLifeLivesCount,
  useGroupLifeProposalDetails,
} from '@/features/proposals/hooks/group-life';
import { parseCurrencyInput } from '@/features/quotes/utils/group-life/format';
import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import {
  coverageUnitPurePremium,
  firstMeaningfulValue,
} from '@/features/quotes/utils/group-life/coverage-unit-premium.util';
import { useGroupLifeUnderwritingMessages } from '@/features/quotes/hooks/group-life/use-group-life-underwriting-messages.hook';
import { useCallback, useMemo } from 'react';
import { usePmeTarifacao } from '../use-pme-tarifacao.hook';
import { dailyAllowanceDetails } from '@/features/quotes/utils/group-life/daily-allowance.util';
import {
  deriveAfastadoRows,
  deriveAgeCapitalRows,
  deriveCoverageRows,
  deriveServiceRows,
} from '../utils/derive-analysis-tabs';
import { usePmeProposalAnalysisMembers } from './use-pme-proposal-analysis-members.hook';

/**
 * View-model da tela "Análise Proposta PME": centraliza todo o fetch (snapshot
 * da proposta, mensagens de subscrição, schema de coberturas e vidas) e as
 * derivações das abas (Critérios, Coberturas, Idade/Capital, Afastados) e
 * seções (prêmio, dados da cotação, estipulantes, comercialização).
 */
/** Vidas por página em cada tabela da "Lista de Vidas". */
export const PME_PROPOSAL_ANALYSIS_LIVES_PAGE_SIZE = 10;

export function usePmeProposalAnalysis(proposalId: string) {
  const detailsQuery = useGroupLifeProposalDetails(proposalId);
  const details = detailsQuery.data;

  const underwritingQuery = useGroupLifeUnderwritingMessages(proposalId);
  const schemaQuery = useGroupLifeCoverageSchema({
    productCode: details?.productCode || undefined,
  });
  const { members, isLoading: isLoadingMembers } =
    usePmeProposalAnalysisMembers(proposalId, details);
  // Contagem de vidas da análise, feita pelo backend (`/members/page`) por
  // target. As tabelas, separadas por estipulante, buscam as próprias páginas.
  const livesTargetIds = useMemo(
    () => [
      '__STIPULATOR__',
      ...(details?.subStipulators ?? []).map(sub => sub.id),
    ],
    [details?.subStipulators]
  );
  const livesCount = useGroupLifeLivesCount({
    policyId: details?.policyId,
    targetIds: livesTargetIds,
  });
  const totalLives = livesCount.total;
  // Soma do capital das vidas, sobre a lista completa que as abas Idade /
  // Capital e Afastados já carregam — sem uma segunda varredura de vidas.
  const totalMemberCapital = useMemo(
    () =>
      members.reduce(
        (acc, member) => acc + (parseCurrencyInput(member.capital) ?? 0),
        0
      ),
    [members]
  );
  const pricingQuery = usePmeTarifacao(
    details?.policyId ?? '',
    'proposal',
    undefined,
    1000
  );

  const underwritingMessages = useMemo(
    () =>
      (underwritingQuery.data?.messages ?? [])
        .map(message =>
          (
            message.messageDetail?.trim() ||
            message.ruleName?.trim() ||
            ''
          ).trim()
        )
        .filter(Boolean),
    [underwritingQuery.data?.messages]
  );

  const premium = useMemo(
    () => mapPremiumValues(details?.calculation),
    [details?.calculation]
  );
  const coverageNameByCode = useMemo(
    () => buildCoverageNameByCode(schemaQuery.data),
    [schemaQuery.data]
  );
  const demonstrativeRows = useMemo(
    () => mapDemonstrativeRows(details?.calculation, coverageNameByCode),
    [details?.calculation, coverageNameByCode]
  );
  const pricingLives = useMemo(
    () => pricingQuery.data?.pages.flatMap(page => page.data) ?? [],
    [pricingQuery.data]
  );
  const sumInsuredByCoverage = useMemo(
    () => coverageUnitSumInsured(pricingLives),
    [pricingLives]
  );
  // Assistência não tem capital — o que ela tem é o prêmio puro ANUAL de uma
  // vida, que o resumo do plano exibe ÷ 12.
  const unitPremiumByCoverage = useMemo(
    () => coverageUnitPurePremium(pricingLives),
    [pricingLives]
  );
  const policyCoverageSumInsured = useMemo(
    () =>
      sumCoverageSumInsured([
        details?.plan?.coverageSumInsured,
        ...(details?.subStipulators ?? []).map(sub => sub.coverageSumInsured),
      ]),
    [details?.plan?.coverageSumInsured, details?.subStipulators]
  );
  const planCoverageGroups = useMemo(
    () =>
      resolvePlanCoverages(schemaQuery.data, details, {
        memberCapitalOverride: totalMemberCapital,
        livesCount: totalLives,
        unitPurePremiumByCoverage: unitPremiumByCoverage,
        coverageSumInsured: policyCoverageSumInsured,
      }),
    [
      schemaQuery.data,
      details,
      totalMemberCapital,
      totalLives,
      unitPremiumByCoverage,
      policyCoverageSumInsured,
    ]
  );
  const accordions = useMemo(
    () =>
      mapStipulantAccordions(details, coverageSumInsured =>
        resolvePlanCoverages(schemaQuery.data, details, {
          memberCapitalOverride: totalMemberCapital,
          livesCount: totalLives,
          unitPurePremiumByCoverage: unitPremiumByCoverage,
          coverageSumInsured,
        })
      ),
    [
      schemaQuery.data,
      details,
      totalMemberCapital,
      totalLives,
      unitPremiumByCoverage,
    ]
  );
  const commercializationRows = useMemo(
    () => mapCommercializationRows(details),
    [details]
  );
  const insuranceCard = useMemo(
    () => mapInsuranceCard(details?.stipulator, details?.effectiveDate),
    [details?.stipulator, details?.effectiveDate]
  );
  const stipulantCard = useMemo(
    () => mapStipulantCard(details?.stipulator),
    [details?.stipulator]
  );

  /**
   * IS fixa das coberturas de seleção de benefício (os auxílios), do catálogo —
   * não depende de a tarifação ter chegado.
   */
  const benefitSumInsured = useMemo(
    () =>
      benefitSumInsuredByCoverage(schemaQuery.data, details?.plan?.coverages),
    [schemaQuery.data, details?.plan?.coverages]
  );

  /**
   * Capital Segurado TOTAL de uma cobertura que o plano não informa — hoje, o
   * auxílio. O catálogo dá o valor de UMA vida (`DefaultSI`), então o total do
   * grupo é ele × vidas; sem a contagem devolve `undefined`, e não zero, para
   * a linha mostrar "—" em vez de um número errado.
   */
  const resolveCoverageCapital = useMemo(
    () =>
      (code: string, name: string): number | undefined => {
        const perLife = firstMeaningfulValue(
          benefitSumInsured.get(code),
          benefitSumInsured.get(name),
          sumInsuredByCoverage.get(code),
          sumInsuredByCoverage.get(name)
        );
        if (perLife == null) return undefined;
        return totalLives > 0 ? perLife * totalLives : undefined;
      },
    [benefitSumInsured, sumInsuredByCoverage, totalLives]
  );

  // Diárias (DH/DIT): "30 diárias × R$ 100,00", da opção gravada no plano.
  const dailyAllowanceByCoverage = useMemo(
    () => dailyAllowanceDetails(schemaQuery.data, details?.plan?.coverages),
    [schemaQuery.data, details?.plan?.coverages]
  );
  const coverageRows = useMemo(
    () =>
      deriveCoverageRows(
        planCoverageGroups,
        demonstrativeRows,
        resolveCoverageCapital,
        dailyAllowanceByCoverage
      ),
    [
      planCoverageGroups,
      demonstrativeRows,
      resolveCoverageCapital,
      dailyAllowanceByCoverage,
    ]
  );
  const serviceRows = useMemo(
    () => deriveServiceRows(planCoverageGroups, demonstrativeRows, totalLives),
    [planCoverageGroups, demonstrativeRows, totalLives]
  );
  const resolveLifeCapital = useMemo(
    () => createLifeCapitalResolver(pricingLives),
    [pricingLives]
  );
  // Múltiplo Salarial: salário × quantidade, depois piso e teto de cada vida.
  // A soma por idade usa esses valores e não é limitada de novo.
  const capitalShown = useCallback(
    (member: (typeof members)[number]) => {
      const resolved = resolveLifeCapital(member);
      if (details?.plan?.modality !== 'MULTIPLE_SALARY') return resolved;
      if (!isStoredSalaryCapital(resolved, member.capital)) {
        return boundDisplayedCapital(resolved, details.plan);
      }
      return (
        resolveMemberDisplayCapital(member.capital, details.plan, {
          multiplySalary: true,
        }) || resolved
      );
    },
    [details?.plan, resolveLifeCapital]
  );
  // O capital exibido vem da tarifação (o que o motor tarifa): segura as
  // tabelas até ela chegar, senão aparece antes o valor do store (salário em
  // Múltiplo Salarial, capital da importação nas demais).
  const isLoadingLifeCapital = isLoadingMembers || pricingQuery.isLoading;
  const ageCapitalRows = useMemo(
    () => deriveAgeCapitalRows(members, capitalShown),
    [members, capitalShown]
  );
  const afastadoRows = useMemo(
    () => deriveAfastadoRows(members, capitalShown),
    [members, capitalShown]
  );
  const criteria = useMemo(
    () =>
      mapUnderwritingMessagesToCriteria(
        underwritingQuery.data,
        isLoadingMembers ? undefined : members,
        { coverageNameByCode }
      ),
    [underwritingQuery.data, isLoadingMembers, members, coverageNameByCode]
  );

  return {
    details,
    isLoading: detailsQuery.isLoading,
    error: detailsQuery.error,
    premium,
    insuranceCard,
    stipulantCard,
    accordions,
    commercializationRows,
    totalLives,
    underwritingMessages,
    coverageRows,
    serviceRows,
    ageCapitalRows,
    afastadoRows,
    isLoadingLifeCapital,
    criteria,
  };
}
